import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Loader2 } from 'lucide-react';
import { api } from '@/utils/api';
import { formatINR } from '@/utils/formatters';
import { formatLakh } from './money';

interface Row {
  kind: 'po' | 'pr'; id: string; number: string; material: string; qty: number; unit: string | null;
  vendorName: string | null; lastRate: number | null; thisRate: number | null; value: number; exGstValue?: number;
  from: { kind: string; label: string }; approval: { band: 'buyer' | 'owner'; label: string };
  state: 'draft' | 'waiting_owners' | 'post_facto' | 'request'; justificationNote: string | null; justificationNoteHidden: boolean;
}

// Same pill style as STATUS_COLORS on PurchaseOrdersPage.
const FROM_PILL: Record<string, string> = {
  below_min: 'bg-yellow-50 text-yellow-700', so_buy_line: 'bg-blue-50 text-blue-700', indent: 'bg-purple-50 text-purple-700',
  quick: 'bg-red-50 text-red-700', manual: 'bg-gray-100 text-gray-600',
};

export function ReleaseQueueView({ onCountChange }: { onCountChange?: (n: number) => void }) {
  const [rows, setRows] = useState<Row[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [loaded, setLoaded] = useState(false);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      const res = await api.get('/purchase-orders/release-queue');
      const data: { rows: Row[]; counts: { total: number } } = res.data?.data ?? { rows: [], counts: { total: 0 } };
      setRows(data.rows);
      setLoaded(true);
      setSelected(previous => new Set(data.rows.filter((r: Row) => r.kind === 'po' && r.state === 'draft' && previous.has(r.id)).map((r: Row) => r.id)));
      onCountChange?.(data.counts.total);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to load the release queue');
    } finally {
      setLoading(false);
    }
  }, [onCountChange]);

  useEffect(() => { void load(); }, [load]);

  const chosen = useMemo(() => rows.filter(r => r.kind === 'po' && selected.has(r.id) && r.state === 'draft'), [rows, selected]);
  const buyerRows = chosen.filter(r => r.approval.band === 'buyer');
  const ownerRows = chosen.filter(r => r.approval.band === 'owner');
  const toggle = (id: string) => setSelected(previous => {
    const next = new Set(previous);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });

  const release = async (group: Row[]) => {
    setBusy(true); setError(null); setDone(null);
    const completed = new Set<string>();
    let issued = 0;
    let sent = 0;
    try {
      for (const r of group) {
        const res = await api.post(`/purchase-orders/${r.id}/release`, { justificationNote: note });
        completed.add(r.id);
        if (res.data.data.outcome === 'sent_to_owners') sent++; else issued++;
      }
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to release');
    } finally {
      // Keep the other approval band selected, and retain failed rows for retry.
      if (completed.size) {
        setSelected(previous => new Set([...previous].filter(id => !completed.has(id))));
        const sentence = (n: number, action: string) => `${n} purchase order${n === 1 ? '' : 's'} ${action}.`;
        setDone([issued ? sentence(issued, 'released') : '', sent ? sentence(sent, 'sent to the owners') : ''].filter(Boolean).join(' '));
        if (chosen.every(r => completed.has(r.id))) setNote('');
        await load();
      }
      setBusy(false);
    }
  };

  if (loading) return <div className="flex justify-center py-12"><Loader2 size={20} className="animate-spin text-[var(--saffron-600)]" /></div>;
  if (!loaded && error) return <p role="alert" className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800">{error}</p>;

  return (
    <div className="rounded-xl border border-gray-200 bg-white">
      {error && <p role="alert" className="border-b border-red-200 bg-red-50 px-4 py-2 text-sm text-red-800">{error}</p>}
      {done && <p role="status" className="border-b border-green-200 bg-green-50 px-4 py-2 text-sm text-green-800">{done}</p>}
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead><tr className="border-b border-gray-100 text-left text-xs font-medium uppercase text-gray-500">
            <th className="w-10 px-4 py-3"><span className="sr-only">Select</span></th>
            {['Material', 'Qty', 'Vendor', 'Last rate', 'This rate', 'Value', 'From', 'Approval'].map(h => (
              <th key={h} className={`px-4 py-3 ${['Qty', 'Last rate', 'This rate', 'Value'].includes(h) ? 'text-right' : ''}`}>{h}</th>
            ))}
          </tr></thead>
          <tbody>
            {rows.length === 0 && <tr><td colSpan={9} className="px-4 py-10 text-center text-gray-400">Nothing waiting for release.</td></tr>}
            {rows.map(r => (
              <tr key={`${r.kind}-${r.id}`} className="border-b border-gray-50 hover:bg-gray-50">
                <td className="px-4 py-3">
                  {r.kind === 'po' && r.state === 'draft' ? (
                    <input type="checkbox" aria-label={`Select ${r.number}`} checked={selected.has(r.id)} disabled={busy} onChange={() => toggle(r.id)} />
                  ) : r.kind === 'pr' ? (
                    <Link to={`/purchase-requests/${r.id}`} className="text-xs font-medium text-[var(--ink-0)] hover:underline">Make PO</Link>
                  ) : null}
                </td>
                <td className="px-4 py-3 text-gray-900">{r.material}</td>
                <td className="px-4 py-3 text-right text-gray-700">{r.qty.toLocaleString('en-IN')}{r.unit ? ` ${r.unit}` : ''}</td>
                <td className="px-4 py-3 text-gray-700">{r.vendorName ?? '—'}</td>
                <td className="px-4 py-3 text-right text-gray-500">{r.lastRate == null ? '—' : formatINR(r.lastRate, false)}</td>
                <td className="px-4 py-3 text-right text-gray-700">{r.thisRate == null ? '—' : formatINR(r.thisRate, false)}</td>
                <td className="px-4 py-3 text-right font-medium text-gray-900">{formatLakh(r.value)}
                  {r.kind === 'po' && r.exGstValue != null && <span className="block text-xs font-normal text-gray-500">{`${formatLakh(r.exGstValue)} before GST`}</span>}
                </td>
                <td className="px-4 py-3"><span className={`rounded-full px-2 py-0.5 text-xs font-medium ${FROM_PILL[r.from.kind] ?? FROM_PILL.manual}`}>{r.from.label}</span></td>
                <td className="px-4 py-3 text-gray-700">{r.approval.label}
                  {r.state === 'waiting_owners' && <span className="ml-2 text-xs text-yellow-700">waiting</span>}
                  {r.state === 'post_facto' && <span className="ml-2 text-xs text-red-700">post-facto</span>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="space-y-3 border-t border-gray-100 p-4">
        <label htmlFor="rq-note" className="block text-xs font-medium text-gray-600">Justification note · required</label>
        <textarea id="rq-note" rows={2} value={note} disabled={busy} onChange={e => setNote(e.target.value)}
          placeholder="Chosen supplier and why, with the other prices you checked"
          className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-[var(--ink-0)] focus:outline-none focus:ring-1 focus:ring-[var(--ink-0)]" />
        <p className="text-xs text-gray-500">Visible to the owners, the buyer and accounts only.</p>
        <div className="flex flex-wrap items-center gap-2">
          <button disabled={busy || buyerRows.length === 0} onClick={() => release(buyerRows)}
            className="rounded-lg bg-[var(--ink-0)] px-4 py-2 text-sm font-medium text-white hover:bg-[var(--ink-1)] disabled:opacity-50">
            {`Release ${buyerRows.length} · ${formatLakh(buyerRows.reduce((sum, r) => sum + r.value, 0))}`}
          </button>
          <button disabled={busy || ownerRows.length === 0} onClick={() => release(ownerRows)}
            className="rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50">
            {`Send ${ownerRows.length} to owners`}
          </button>
          <span className="text-xs text-gray-500">Up to ₹20 L before GST, release is the approval.</span>
        </div>
      </div>
    </div>
  );
}
